import { pluck, prop, sum, uniqBy } from 'ramda';
import { dequal } from 'dequal';
import { Answer, Question, Visit } from '../types/types';
import { formatArrayIntoSentence, formatToFullAddress, US_STATES } from '../utils/utils';

/**
 *  ----------------------------------------------------------------------------
 *  ----------------------------------------------------------------------------
 * This has nothing to do with the conventional Context API.
 * It just contains helper functions for actions such as api calls.
 * If you find a better way to group this functions or methods feel free to
 * refactor and improve the code.
 *
 * Please don't misuse the FREEDOM 😉
 * ----------------------------------------------------------------------------
 * ----------------------------------------------------------------------------
 */

export const VISIT_STATUS = {
  COMPLETE: 'COMPLETED',
  UNANSWERED: 'IN-PROGRESS',
} as const;

export const LAB_TEST_STATUS = {
  REQUESTED: 'REQUESTED',
  SENT_TO_LAB: 'SENT-TO-LAB',
  RECEIVED_REPORT: 'RECEIVED-REPORT',
};

export const VISIT_TYPE_CONSTANTS = {
  BIRTH_CONTROL: 'BIRTH-CONTROL',
  SICK_VISITS: 'SICK',
  UTI_VISITS: 'UTI',
  SEXUAL_HEALTH_VISITS: 'SEXUAL-HEALTH',
  STD_DIAGNOSIS_VISITS: 'STD-DIAGNOSIS',
  STD_TEST_VISITS: 'STD-TEST',
  COLD_SORES_VISITS: 'COLD-SORES',
  GENITAL_HERPES_VISITS: 'GENITAL-HERPES',
  BEHAVIORAL_VISITS: 'MENTAL-HEALTH',
  HAIR_SKIN_VISITS: 'HAIR-SKIN',
  VAGINAL_VISITS: 'VAGINAL-INFECTIONS',
  MENSTRUAL_VISITS: 'MENSTRUAL-IRREGULARITIES',
  ERECTILE_VISITS: 'ERECTILE-DYSFUNCTION',
  RX_REFILLS: 'RX-REFILLS',
  SOMETHING_ELSE: 'SOMETHING-ELSE',
};

export const visitTypes: Visit[] = [
  {
    name: 'Birth Control',
    type: VISIT_TYPE_CONSTANTS.BIRTH_CONTROL,
    tabs: {
      patientInfo: {
        name: 'Patient Info',
        questionKeys: ['FULL_NAME', 'DATE_OF_BIRTH', 'ADDRESS'],
      },
      healthHistory: {
        name: 'Health History',
        questionKeys: [
          'CURRENT_MEDICATION_SUPPLEMENTS',
          'PREGNANT_OR_BREASTFEEDING',
          'SMOKER',
          'DRUG_ALLERGIES',
          'DRUG_ALLERGIES_LIST',
          'MEDICAL_CONDITIONS',
          'SCHEDULED_SURGERY',
          'HEIGHT',
          'WEIGHT',
          'BLOOD_PRESSURE_READING',
          'PAST_BIRTH_CONTROL',
          'PAST_PRESCRIPTION',
          'FREQUENT_MIGRAINES',
          'USE_OF_HORMONAL_CONTRACEPTIVES',
          'MAIN_BIRTH_CONTROL_REASON',
          'IDENTIFICATION',
        ],
      },
    },
  },
  {
    name: 'Sick',
    type: VISIT_TYPE_CONSTANTS.SICK_VISITS,
    tabs: {
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'WHEN_SYMPTOMS_STARTED',
          'DESCRIBE_SYMPTOMS',
          'EXPERIENCED_SYMPTOMS_BEFORE',
          'CURRENT_MEDICATION',
        ],
      },
      additionalInfo: {
        name: 'Additional Info',
        questionKeys: ['OTHER_INFO'],
      },
    },
  },
  {
    name: 'UTI Treatment',
    type: VISIT_TYPE_CONSTANTS.UTI_VISITS,
    tabs: {
      patientInfo: {
        name: 'Patient Info',
        questionKeys: [
          'AGE_OLD',
          'GENDER_TYPE',
          'ASSIGNED_SEX_AT_BIRTH',
          'LAST_PERIOD',
          'PREGNANT_OR_POST_MENOPAUSAL',
        ],
      },
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'SEXUALLY_ACTIVE',
          'VAGINAL_DISCHARGE',
          'STD_TEST_LAST_MONTH',
          'DISCOMFORT_WHEN_URINATING',
          'BLOOD_IN_URINE',
          'CLOUDY_URINE',
          'EXPERIENCING_BLADDER_PAIN',
          'FLANK_PAIN',
          'FEVER_CHILLS',
          'NAUSEOUS_VOMITING',
          'INVOLUNTARY_URINATION',
          'TREATED_FOR_UTI',
          'CURRENTLY_ON_ANTIBIOTICS',
          'IMMUNOSUPPRESSIVE_MEDICATIONS',
          'URINARY_CATHETER',
          'ANY_SURGERIES',
        ],
      },
    },
  },
  {
    name: 'Vaginal Infections',
    type: VISIT_TYPE_CONSTANTS.VAGINAL_VISITS,
    tabs: {
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'AGE_OLD',
          'SEXUALLY_ACTIVE',
          'DIAGNOSED_WITH_STD',
          'STD_DIAGNOSIS_TYPE',
          'EXPERIENCING_ITCHING',
          'VAGINAL_RASH',
          'BURNING_SENSATION',
          'VULVA_RED_SWOLLEN',
          'VAGINAL_DISCHARGE',
        ],
      },
      healthHistory: {
        name: 'Health History',
        questionKeys: [
          'DIAGNOSED_YEAST_INFECTION',
          'YEAST_INFECTION_PERIOD',
          'PREGNANT',
          'CURRENT_MEDICATIONS',
          'OTHER_HEALTH_ISSUES',
        ],
      },
    },
  },
  {
    name: 'Erectile Dysfunction',
    type: VISIT_TYPE_CONSTANTS.ERECTILE_VISITS,
    tabs: {
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'AGE_OLD',
          'DURATION_OF_ERECTILE_DYSFUNCTION',
          'START_OF_ERECTILE_PROBLEMS',
          'RELATION_TO_PROBLEMS',
          'PARTIAL_OR_FULL_ERECTIONS',
          'PENETRATE_DURING_SEX',
          'MAINTAIN_ERECTION_DURING_SEX',
          'SEXUAL_DESIRE_RATING',
          'ACTIVE_SEXUAL_PARTNER',
          'ACHIEVE_AN_ORGASM',
          'EJACULATE_NORMALLY',
          'PREMATURE_EJACULATION',
          'EMOTIONAL_CASE_WITH_ERECTION',
          'PAIN_WITH_ERECTIONS',
          'ERECTIONS_BENT',
          'ERECTIONS_BENT_DIRECTION',
        ],
      },
      medicalHistory: {
        name: 'Medical History',
        questionKeys: [
          'TESTOSTERONE_LEVEL_MEASURED',
          'RESULTS_FOR_TESTOSTERONE_LEVEL',
          'PENILE_INJECTION',
          'PENILE_INJECTION_EFFECT',
          'TRIED_AN_ENHANCER',
          'DID_ENHANCER_WORKED',
          'OTHER_TREATMENTS',
          'TREATMENT_METHOD',
          'TREATED_FOR_DIABETES',
          'TREATMENT_METHOD_FOR_SUGAR',
          'TREATED_FOR_BLOOD_PRESSURE',
          'TREATED_FOR_CHOLESTEROL_LEVEL',
          'HEART_DISEASE',
          'HAD_A_STROKE',
          'HARDENING_OF_ARTERIES',
          'TREATED_FOR_DEPRESSION',
          'SURGERIES_IN_PAST',
          'OTHER_MEDICAL_CONDITION',
          'OTHER_MEDICAL_CONDITION_DESCRIPTION',
        ],
      },
    },
  },
  {
    name: 'Menstrual Irregularities',
    type: VISIT_TYPE_CONSTANTS.MENSTRUAL_VISITS,
    tabs: {
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'LAST_MENSTRUAL_PERIOD',
          'DURATION_OF_MENSTRUAL_PERIOD',
          'CURRENTLY_ON_MENSTRUAL_PERIOD',
          'EXPERIENCING_MENSTRUAL_CRAMPS',
          'PAIN_RATING',
          'EXPERIENCING_PAIN_SOME_BODY_PARTS',
          'ANXIOUS',
          'HEADACHE',
          'LAST_PAP_SMEAR',
          'ABNORMAL_PAP',
          'ABNORMAL_PAP_PERIOD',
        ],
      },
      pastHistory: {
        name: 'Past History',
        questionKeys: [
          'SEXUALLY_ACTIVE',
          'DIAGNOSED_WITH_STI',
          'DIAGNOSIS',
          'TAKING_BIRTH_CONTROL',
          'TYPE_OF_BIRTH_CONTROL',
          'BIRTH_CONTROL_SIDE_EFFECTS',
          'OTHER_SIDE_EFFECTS',
        ],
      },
    },
  },
  {
    name: 'STD Diagnosis',
    type: VISIT_TYPE_CONSTANTS.STD_DIAGNOSIS_VISITS,
    tabs: {
      sexualHistory: {
        name: 'Sexual History',
        questionKeys: [
          'DIAGNOSIS',
          'OTHER_DIAGNOSIS',
          'PERIOD_FOR_DIAGNOSIS',
          'MEDICATION_FOR_DIAGNOSIS',
          'TYPE_OF_MEDICATION',
        ],
      },
      symptoms: {
        name: 'Symptoms',
        questionKeys: ['SYMPTOMS_FROM_DIAGNOSIS', 'DESCRIBE_SYMPTOMS'],
      },
    },
  },
  {
    name: 'STD Test',
    type: VISIT_TYPE_CONSTANTS.STD_TEST_VISITS,
    tabs: {
      currentRelationships: {
        name: 'Current Relationships',
        questionKeys: ['SEXUALLY_ACTIVE', 'SEX_WITH_PARTNER_DIAGNOSED'],
      },
      sexualHistory: {
        name: 'Sexual History',
        questionKeys: [
          'PARTNER_DIAGNOSIS',
          'OTHER_DIAGNOSIS',
          'TESTED_FOR_STD_14_DAYS',
          'STD_TEST_STATUS',
        ],
      },
    },
  },
  {
    name: 'Hair & Skin',
    type: VISIT_TYPE_CONSTANTS.HAIR_SKIN_VISITS,
    tabs: {
      getStarted: {
        name: 'Get Started',
        questionKeys: ['TYPE_SKIN_HAIR_CONDITION'],
      },
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'EXPERIENCING_ANY_OTHER_RELATED_ISSUES',
          'FIRST_NOTICED_CONDITION',
          'HAIR_LOSS_ON_YOUR_SCALP',
          'CONDITION_LOCATION_ON_BODY',
          'OFTEN_NOTICE_OF_CONDITION',
          'OTHER_ASSOCIATED_SYMPTOMS',
          'CONDITION_GETS_WORSEN',
          'CONDITION_OF_HAIR_LOSS',
          'HAIR_LOSS_ONSET',
          'HAIR_LOSS_CONDITION_TYPE',
          'SCALP_ITCHING_OR_FLAKING',
          'DIET_CHANGED',
          'ON_HORMONE_TREATMENT',
          'TYPE_OF_HORMONE_TREATMENT',
          'PREVIOUS_TREATMENT',
          'PREVIOUS_TREATMENT_TYPE',
          'HISTORY_OF_ANY_CONDITIONS_AND_LIFESTYLE',
          'HISTORY_OF_FAMILY',
          'OTHER_HEALTH_ISSUES',
          'PHOTO_OF_CONDITION',
        ],
      },
    },
  },
  {
    name: 'Behavioral Health',
    type: VISIT_TYPE_CONSTANTS.BEHAVIORAL_VISITS,
    tabs: {
      getStarted: {
        name: 'Get Started',
        questionKeys: ['TYPE_BEHAVIORAL_HEALTH'],
      },
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'SYMPTOMS_DURATION',
          'LITTLE_INTEREST',
          'FEELING_DOWN',
          'TROUBLE_SLEEPING',
          'FAILURE_ISSUES',
          'TROUBLE_CONCENTRATING',
          'RESTLESS',
          'HURTFUL_THOUGHTS',
          'ANXIETY_FEELING_NERVOUS',
          'ANXIETY_CONTROL_WORRYING',
          'ANXIETY_WORRYING_TOO_MUCH',
          'ANXIETY_TROUBLE_RELAXING',
          'ANXIETY_RESTLESS',
          'ANXIETY_EASILY_ANNOYED',
          'ANXIETY_FEELING_AFRAID',
          'TAKING_MEDICATION',
        ],
      },
    },
  },
  {
    name: 'Genital Herpes',
    type: VISIT_TYPE_CONSTANTS.GENITAL_HERPES_VISITS,
    tabs: {
      patientInfo: {
        name: 'Patient Info',
        questionKeys: ['AGE_OLD', 'ASSIGNED_SEX_AT_BIRTH'],
      },
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'PREVIOUSLY_GENITAL_HERPES',
          'TESTED_POSITIVE_FOR_HIV',
          'SEXUALLY_ACTIVE',
          'ENGAGED_IN_ORAL_SEX',
          'TESTED_POSITIVE_STI',
          'TYPE_OF_STI',
          'HISTORY_OF_COLD_SORES',
          'HAVE_GENITAL_ULCERS',
          'PAINFUL_GENITAL_ULCERS',
          'PAIN_CONDITION',
          'ITCHY_GENITAL_ULCERS',
          'HAVE_TENDER_LYMPH_NODES',
          'HAVE_FEVER',
          'CURRENTLY_TIRED',
          'MUSCLE_ACHES',
          'EXPERIENCING_HEADACHES',
          'DIFFICULT_TO_URINATE',
          'PAIN_IN_URINE',
          'VAGINAL_DISCHARGE',
          'PENILE_DISCHARGE',
          'PAIN_AT_PELVIC_AREA',
        ],
      },
    },
  },
  {
    name: 'Cold Sores',
    type: VISIT_TYPE_CONSTANTS.COLD_SORES_VISITS,
    tabs: {
      getStarted: {
        name: 'Get Started',
        questionKeys: ['AGE_OLD', 'NOTICED_COLD_SORES'],
      },
      symptoms: {
        name: 'Symptoms',
        questionKeys: [
          'CONDITION_OF_COLD_SORES',
          'PREVIOUS_COLD_SORES',
          'COLD_SORE_OUTBREAK',
          'DIAGNOSED_STI',
          'WHAT_WAS_THE_DIAGNOSIS',
          'TREATED_FOR_STI',
          'EXPERIENCING_SYMPTOMS',
          'OTHER_INFO',
          'PHOTO_OF_CONDITION',
        ],
      },
    },
  },
  {
    name: 'Rx Refills',
    type: VISIT_TYPE_CONSTANTS.RX_REFILLS,
    tabs: {
      requestInfo: {
        name: 'Request Info',
        questionKeys: [
          'MEDICATION_PRESCRIBED_BY',
          'MEDICATION_TO_REFILL',
          'MEDICATION_NAME',
          'MEDICATION_DOSAGE',
          'PHOTO_OF_MEDICATION',
          'MEDICAL_HISTORY_CHANGES',
          'ABOUT_HEALTH_CHANGES',
          'NEW_ALLERGIES',
          'ABOUT_NEW_ALLERGIES',
        ],
      },
      mailingAddress: {
        name: 'Mailing Address',
        questionKeys: ['ADDRESS'],
      },
    },
  },
];

export const visitIconsAndColors: Record<
  string,
  {
    icon: string;
    color: string;
    bgColor: string;
  }
> = {
  'birth-control': {
    icon: 'iconChatCardBirthControlPill',
    color: '#2FAEF3',
    bgColor: 'rgb(229, 246, 255)',
  },
  sick: {
    icon: 'iconChatCardSickVisitPill',
    color: '#FF8C42',
    bgColor: '#FFE6D6',
  },
  uti: {
    icon: 'iconChatCardUTI',
    color: '#F4ACB8',
    bgColor: '#FFE1E6',
  },
  'std-diagnosis': {
    icon: 'iconChatCardSexualHealth',
    color: '#FFC26F',
    bgColor: '#FFF6E7',
  },
  'std-test': {
    icon: 'iconChatCardSexualHealth',
    color: '#FFC26F',
    bgColor: '#FFF6E7',
  },
  'cold-sores': {
    icon: 'iconChatCardSexualHealth',
    color: '#FFC26F',
    bgColor: '#FFF6E7',
  },
  'genital-herpes': {
    icon: 'iconChatCardSexualHealth',
    color: '#FFC26F',
    bgColor: '#FFF6E7',
  },
  'mental-health': {
    icon: 'iconChatCardMentalHealthTreatment',
    color: '#d8ae39',
    bgColor: 'rgb(255, 245, 209)',
  },
  'hair-skin': {
    icon: 'iconChatCardHairSkin',
    color: '#68C5A3',
    bgColor: 'rgb(222, 255, 243)',
  },
  'vaginal-infections': {
    icon: 'iconChatCardVaginalInfections',
    color: '#B760FF',
    bgColor: '#F4E7FF',
  },
  'menstrual-irregularities': {
    icon: 'iconChatCardMenstrualIrregularities',
    color: '#FFAE2D',
    bgColor: 'rgb(255, 246, 231)',
  },
  'erectile-dysfunction': {
    icon: 'iconChatCardErectileDysfunction',
    color: '#EAA37D',
    bgColor: 'rgb(255, 238, 227)',
  },
  'rx-refills': {
    icon: 'iconChatCardRxRefill',
    color: '#2FAEF3',
    bgColor: 'rgb(229, 246, 255)',
  },
  lab: {
    icon: 'iconLabReportDocument',
    color: '#2FAEF3',
    bgColor: 'rgb(229, 246, 255)',
  },
};

export const getRelatedAnswerToDisplay = (questions: Question[], answers: Answer[]) => {
  const findAnswer =
    answers.find(ans =>
      [
        'DIAGNOSIS',
        'OTHER_DIAGNOSIS',
        'TYPE_BEHAVIORAL_HEALTH',
        'TYPE_SKIN_HAIR_CONDITION',
      ].includes(ans.questionKey),
    )?.answers[0] || '';

  const findQuestion = questions.find(ques =>
    ['DIAGNOSIS', 'OTHER_DIAGNOSIS', 'TYPE_BEHAVIORAL_HEALTH', 'TYPE_SKIN_HAIR_CONDITION'].includes(
      ques.id,
    ),
  );

  if (findQuestion?.options && findQuestion?.options.length > 0) {
    const answerToDisplay = findQuestion.options
      ?.filter(child => child.id === findAnswer)
      .map(ques => ques.text) as string[];

    if (answerToDisplay && answerToDisplay.length > 0) {
      return formatArrayIntoSentence(answerToDisplay);
    }
  }

  // Support Legacy Questions
  if (findQuestion?.children && findQuestion?.children.length > 0) {
    const answerToDisplay = findQuestion.children
      ?.filter(child => child.id === findAnswer)
      .map(ques => ques.option) as string[];

    if (answerToDisplay && answerToDisplay.length > 0) {
      return formatArrayIntoSentence(answerToDisplay);
    }
  }

  return findAnswer;
};

export const mappedQuesToAnsT: Record<
  string,
  (
    question: Question,
    answers: Answer[],
    answerToDisplay?: string,
  ) => {
    quesText: string;
    quesAnswer: string;
    quesOptions?: string;
  }
> = {
  'birth-control': mappedBCQuesToAns,
  sick: mappedSickQuesToAns,
  uti: mappedUTIQuesToAns,
  'std-diagnosis': mappedSTDDiagQuesToAns,
  'std-test': mappedStdTestQuesToAns,
  'cold-sores': mappedColdSoresQuesToAns,
  'genital-herpes': mappedGenHerpesQuesToAns,
  'mental-health': mappedBehavioralQuesToAns,
  'hair-skin': mappedHairSkinQuesToAns,
  'vaginal-infections': mappedVagInfecQuesToAns,
  'menstrual-irregularities': mappedMenstrualQuesToAns,
  'erectile-dysfunction': mapErectileDysQuestionsToAnswer,
  'rx-refills': mappedRxRefillsQuesToAns,
  'follow-up': mappedFollowUpQuesToAns,
};

function mappedBCQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'ADDRESS') {
    // quesOption = 'Address';
    // ansOption =
    //   answer?.answers && answer?.answers?.length ? formatToFullAddress(answer.answers) : '';
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  if (question.id === 'BLOOD_PRESSURE_READING') {
    quesOption = 'What is your blood pressure reading? (Systolic and Diastolic)';
  }

  if (question.id === 'IDENTIFICATION') {
    quesOption = 'Photo';
  }

  if (['FULL_NAME', 'DATE_OF_BIRTH'].includes(question.id)) {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'MEDICATION_MAILING_DETAILS') {
    ansOption = ansOption.replace('[insert mailing address]', '');
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedSickQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '-'; // we set ansOption to '-' if no answer

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'CURRENT_MEDICATION') {
    quesOption = 'Medications';
  }

  // if (question.id === 'ADDRESS') {
  //   quesOption = 'Address';
  //   ansOption =
  //     answer?.answers && answer?.answers?.length ? formatToFullAddress(answer.answers) : '';
  // }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedUTIQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (['AGE_OLD', 'GENDER_TYPE'].includes(question.id)) {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedSTDDiagQuesToAns(question: Question, answers: Answer[], answerToDisplay?: string) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.id === 'TYPE_OF_MEDICATION') {
    quesOption = 'Medications';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'DIAGNOSIS' && ansOption === 'Something else') {
    quesOption = '';
    ansOption = '';
  }

  return {
    quesText: quesOption
      .replace('**{insert selected STD}**', answerToDisplay || 'this STI')
      .replace('[insert selected STD]', answerToDisplay || 'this STI'),
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedStdTestQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'PARTNER_DIAGNOSIS' && ansOption === 'SOMETHING_ELSE') {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'OTHER_DIAGNOSIS' && ansOption !== '-') {
    quesOption = 'What was your sexual partner(s) diagnosed with?';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedColdSoresQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'AGE_OLD') {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'PHOTO_OF_CONDITION') {
    quesOption = 'Photo';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedGenHerpesQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'AGE_OLD') {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'ASSIGNED_SEX_AT_BIRTH') {
    quesOption = 'What was your assigned sex at birth?';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedBehavioralQuesToAns(
  question: Question,
  answers: Answer[],
  answerToDisplay?: string,
) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '-'; // we set ansOption to '-' if no answer

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  let responseQuesToDisplay = '';
  let optionQuesToDisplay = '';

  if (answerToDisplay === 'Anxiety' || answerToDisplay?.includes('Anxious')) {
    responseQuesToDisplay = 'feeling anxious';
    optionQuesToDisplay = 'Anxiety';
  }

  if (answerToDisplay === 'Depression' || answerToDisplay?.includes('Depressed')) {
    responseQuesToDisplay = 'feeling depressed';
    optionQuesToDisplay = 'Depression';
  }

  if (question.id === 'DETAILS_OF_TAKING_COUNTER_MEDICATIONS') {
    quesOption = 'Medications';
  }

  if (question.id === 'OTHER_DIAGNOSIS' && ansOption !== '-') {
    quesOption = 'What was your sexual partner(s) diagnosed with?';
  }

  if (question.id === 'TYPE_BEHAVIORAL_HEALTH') {
    if (ansOption === 'Anxiety' || answerToDisplay?.includes('Anxious')) {
      ansOption = 'Anxious';
    }

    if (ansOption === 'Depression' || answerToDisplay?.includes('Depressed')) {
      ansOption = 'Depressed';
    }

    quesOption = 'How are you feeling today?';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  if (ansOption === '-') {
    return { quesText: '', quesAnswer: '', quesOptions: '' };
  }

  return {
    quesText: quesOption.replace('[feeling symptoms]', responseQuesToDisplay),
    quesAnswer: ansOption.replace('[feeling symptoms]', optionQuesToDisplay),
    quesOptions: '',
  };
}

function mappedHairSkinQuesToAns(question: Question, answers: Answer[], answerToDisplay?: string) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  let conditionTypeAnswerToDisplay = answerToDisplay;

  if (answerToDisplay === 'Something Else') {
    conditionTypeAnswerToDisplay = 'condition';
  }

  if (question.id === 'PHOTO_OF_CONDITION') {
    quesOption = 'Photo';
  }

  if (question.id === 'TYPE_SKIN_HAIR_CONDITION') {
    quesOption = 'What Hair or Skin condition are you experiencing?';

    if (ansOption === 'SOMETHING_ELSE') {
      quesOption = '';
      ansOption = '';
    }
  }

  if (question.id === 'EXPERIENCING_ANY_OTHER_RELATED_ISSUES') {
    quesOption = 'What Hair or Skin condition are you experiencing?';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  if (!ansOption || ansOption === '-') {
    return { quesText: '', quesAnswer: '', quesOptions: '' };
  }

  return {
    quesText: quesOption.replace(
      '**{insert selected condition}**',
      conditionTypeAnswerToDisplay || '',
    ),
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedVagInfecQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'CURRENT_MEDICATIONS') {
    const medications = ansOption === 'Yes' ? '' : 'None';
    quesOption = 'Medications';
    ansOption = medications;
  }

  if (question.id === 'AGE_OLD') {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedMenstrualQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  const quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  if (ansOption === '-') {
    return { quesText: '', quesAnswer: '', quesOptions: '' };
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mapErectileDysQuestionsToAnswer(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'AGE_OLD') {
    quesOption = '';
    ansOption = '';
  }

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedRxRefillsQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] || '';

  if (question.id === 'ADDRESS') {
    ansOption =
      US_STATES.find(state => state.value === ansOption)?.label ||
      formatToFullAddress(answer?.answers || []);
  }

  if (question.id === 'PHOTO_OF_MEDICATION') {
    quesOption = 'Photo';
  }

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  // Support Legacy Questions
  if (question.children && question.children.length && answer?.answers) {
    const listOfOptions = pluck(
      'option',
      question.children.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (
    [
      'MEDICATION_PRESCRIBED_BY',
      'ANOTHER_PROVIDER_NAME',
      'MEDICATION_NAME',
      'MEDICATION_DOSAGE',
      'MEDICATION_REASON',
      'MEDICATION_DURATION',
      'MEDICATION_PILLS_DAYS_LEFT',
      'PHOTO_OF_MEDICATION',
    ].includes(question.id)
  ) {
    const medAnsOption = answer?.answers
      ?.map(findAnswer => {
        let getAnsOption = { [question.id]: findAnswer === 'NONE' ? '' : findAnswer };

        if (question?.options && question?.options.length > 0) {
          const listOfOptions = pluck(
            'text',
            question?.options.filter(child => findAnswer === child.id),
          );

          getAnsOption = {
            [question.id]:
              listOfOptions.length === 0
                ? answer?.answers.join(', ')
                : formatArrayIntoSentence(listOfOptions),
          };
        }

        return getAnsOption;
      })
      .filter(findAnswer => {
        return !dequal(findAnswer, { PHOTO_OF_MEDICATION: 'NONE' });
      });

    ansOption = JSON.stringify(medAnsOption);
  }

  if (question.id === 'MEDICATION_MAILING_DETAILS') {
    ansOption = ansOption.replace('[insert mailing address]', '');
  }

  if (question.id === 'CURRENT_MEDICATIONS' && answer?.answers) {
    ansOption = answer?.answers[0] === 'OTHER' ? 'Other' : formatArrayIntoSentence(answer?.answers);
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
    quesOptions: '',
  };
}

function mappedFollowUpQuesToAns(question: Question, answers: Answer[]) {
  const answer = answers.find(fndAnswer => fndAnswer.questionKey === question.id);

  let quesOption = question?.text || question?.option;

  let ansOption = answer?.answers?.[0] ? answer?.answers?.[0] : '';

  if (question.options && question.options.length && answer?.answers) {
    const listOfOptions = pluck(
      'text',
      question.options.filter(child => {
        let fndAnswers = answer.answers;
        if (fndAnswers.length === 1) {
          fndAnswers = fndAnswers[0].split(',').map(fndAnswer => fndAnswer.trim());
        }
        return fndAnswers.includes(child.id);
      }),
    );

    ansOption =
      listOfOptions.length === 0
        ? answer.answers.join(', ')
        : formatArrayIntoSentence(listOfOptions);
  }

  if (question.id === 'REASON_FOR_FOLLOW_UP') {
    quesOption = 'Reason for follow up';
  }

  if (question.id === 'MORE_INFORMATION') {
    quesOption = 'More information';
  }

  return {
    quesText: quesOption,
    quesAnswer: ansOption,
  };
}

export function extractSubQuestions(questions: Question[]): Question[] {
  const followups: Question[] = [];
  const stack: Question[] = [...questions];

  while (stack.length > 0) {
    const question = stack.pop();
    if (question && question.options) {
      question.options.forEach(option => {
        if (option.followups) {
          followups.push(...option.followups);
          stack.push(...option.followups);
        }
      });
    }
  }

  return uniqBy(prop('id'), followups);
}

export function flattenQuestions(questions: Question[]): Question[] {
  const flattenedList: Question[] = [];
  const stack: Question[] = [...questions];

  while (stack.length > 0) {
    const question = stack.shift() as Question;
    flattenedList.push(question);

    if (question.options && question.options.length > 0) {
      for (let i = question.options.length - 1; i >= 0; i--) {
        const option = question.options[i];
        if (option.followups && option.followups.length > 0) {
          stack.unshift(...option.followups.reverse());
        }
      }
    }
  }

  return flattenedList;
}

export const getGAD7Score = (
  responses: string[],
): {
  value: number;
  status: string;
} => {
  const answerScores = responses.map(fndResponse => {
    if (fndResponse === 'SEVERAL_DAYS' || fndResponse === 'Several days') {
      return 1;
    }

    if (fndResponse === 'HALF_DAYS' || fndResponse === 'More than half the days') {
      return 2;
    }

    if (fndResponse === 'EVERY_DAY' || fndResponse === 'Nearly every day') {
      return 3;
    }

    return 0;
  }) as number[];

  let scoreStatus = 'Minimal Anxiety';

  const totalScores = sum(answerScores);

  // 0-4 Result: Minimal Anxiety
  if (totalScores <= 4) {
    scoreStatus = 'Minimal Anxiety';
  }

  // 5-9 Result: Mild Anxiety
  if (totalScores > 4 && totalScores < 10) {
    scoreStatus = 'Mild Anxiety';
  }

  // 10-14 Result: Moderate
  if (totalScores > 9 && totalScores < 15) {
    scoreStatus = 'Moderate Anxiety';
  }

  // // 15 - 21 Result: Severe
  if (totalScores > 14) {
    scoreStatus = 'Severe Anxiety';
  }

  return {
    value: totalScores,
    status: scoreStatus,
  };
};

export const getPHQ9Score = (
  responses: string[],
): {
  value: number;
  status: string;
} => {
  const answerScores = responses.map(fndResponse => {
    if (fndResponse === 'SEVERAL_DAYS' || fndResponse === 'Several days') {
      return 1;
    }

    if (fndResponse === 'HALF_DAYS' || fndResponse === 'More than half the days') {
      return 2;
    }

    if (fndResponse === 'EVERY_DAY' || fndResponse === 'Nearly every day') {
      return 3;
    }

    return 0;
  }) as number[];

  let scoreStatus = 'Minimal Depression';

  const totalScores = sum(answerScores);

  // 0-4 Result: Minimal Anxiety
  if (totalScores <= 4) {
    scoreStatus = 'Minimal Depression';
  }

  // 5-9 Result: Mild Anxiety
  if (totalScores > 4 && totalScores < 10) {
    scoreStatus = 'Mild Depression';
  }

  // 10-14 Result: Moderate
  if (totalScores > 9 && totalScores < 15) {
    scoreStatus = 'Moderate Depression';
  }

  // 15-19 Result: Moderately
  if (totalScores > 14 && totalScores < 20) {
    scoreStatus = 'Moderately Severe Depression';
  }

  // 20+ Result: Severe
  if (totalScores > 19) {
    scoreStatus = 'Severe Depression';
  }

  return {
    value: totalScores,
    status: scoreStatus,
  };
};

export const GAD7QuestionKeys = [
  'FEELING_ANXIOUS_OR_EDGE',
  'UNABLE_TO_STOP_WORRYING',
  'UNABLE_TO_STOP_WORRYING_TOO_MUCH',
  'TROUBLE_RELAXING',
  'RESTLESS_HARD_TO_SIT',
  'EASILY_ANNOYED_OR_IRRITABLE',
  'FEELING_AFRAID_MIGHT_HAPPEN',
];

export const PHQ9QuestionKeys = [
  'LITTLE_INTEREST_IN_THINGS',
  'FEELING_DOWN_DEPRESSED_HOPELESS',
  'TROUBLE_SLEEPING_OR_SLEEPING_TOO_MUCH',
  'FEELING_TIRED_OR_LITTLE_ENERGY',
  'POOR_APPETITE_OR_OVEREATING',
  'FEELING_BAD_OF_YOURSELF',
  'TROUBLE_CONCENTRATING_THINGS',
  'SLOWLY_OR_MOVING_FAST_WITH_THINGS',
  'THOUGHTS_OF_YOUR_DEATH_OR_HURT',
];
